import styled from 'styled-components';
import { StyledProps } from 'types/styles';
import { variant, borderRadius, space, fontSize, fontWeight, layout, flexbox, color, border } from 'styled-system';

interface Props extends StyledProps {
  to?: string;
  borderColor?: string;
}

export const Wrapper = styled('button')<Props>(
  {
    appearance: 'none',
    fontFamily: 'inherit',
    display: 'flex',
    cursor: 'pointer',
    userSelect: 'none',
  },
  borderRadius,
  border,
  color,
  space,
  fontSize,
  fontWeight,
  fontWeight,
  layout,
  flexbox,
  variant({
    variants: {
      primary: {
        color: 'white',
        bg: 'blacks.0',
        borderColor: 'blacks.0',
        '&:hover': {
          opacity: '0.85',
        },
        '&:disabled': {
          opacity: '0.75',
          cursor: 'not-allowed',
          color: 'white',
        },
      },
      secondary: {
        color: 'blacks.1',
        bg: 'greys.4',
        borderColor: 'greys.4',
        '&:hover': {
          borderColor: 'greys.3',
          backgroundColor: 'greys.3',
        },
        '&:disabled': {
          borderColor: 'greys.5',
          backgroundColor: 'greys.5',
          color: 'blacks.1',
        },
      },
      custom: {
        color: 'greens.2',
        borderColor: 'transparent',
        '&:hover': {
          opacity: '0.95',
        },
        '&:disabled': {
          opacity: '0.55',
          cursor: 'not-allowed',
        },
      },
      highlight: {
        color: 'greens.3',
        bg: 'greens.5',
        borderColor: 'greens.5',
        '&:hover': {
          borderColor: 'greens.5',
          backgroundColor: 'greens.5',
        },
        '&:disabled': {
          borderColor: 'greens.5',
          backgroundColor: 'greens.5',
        },
      },
      oauth: {
        color: 'blacks.2',
        bg: 'whites.0',
        borderColor: 'sands.1',
        '&:hover': {
          borderColor: 'sands.0',
          backgroundColor: 'whites.0',
        },
        '&:disabled': {
          borderColor: 'greys.2',
          backgroundColor: 'whites.0',
        },
      },
      outline: {
        color: 'blacks.0',
        bg: 'white',
        borderColor: 'blacks.0',
        '&:hover': {
          color: 'teals.0',
          borderColor: 'teals.0',
        },
      },
      outlineWhite: {
        color: 'white',
        bg: 'blacks.0',
        borderColor: 'white',
        '&:hover': {
          color: 'aquas.0',
          borderColor: 'aquas.0',
        },
        '&:disabled': {
          color: 'greys.2',
          borderColor: 'greys.2',
          opacity: '0.75',
          cursor: 'not-allowed',
        },
      },
      underline: {
        color: '#6A6969',
        bg: 'white',
        border: 'none',
        borderRadius: 0,
        p: 0,
        m: 0,
        height: 'auto',
        borderBottom: '1.5px solid #6A6969',
        '&:hover': {
          color: 'blacks.0',
        },
      },
      transparent: {
        color: 'greens.0',
        bg: 'inherit',
        borderColor: 'transparent',
      },
      error: {
        color: 'white',
        bg: 'reds.2',
        borderColor: 'transparent',
        '&:hover': {
          opacity: '0.95',
        },
        '&:disabled': {
          opacity: '0.75',
          cursor: 'not-allowed',
        },
      },
      danger: {
        color: 'white',
        bg: 'reds.2',
        borderColor: 'transparent',
        '&:hover': {
          opacity: '0.95',
        },
        '&:disabled': {
          opacity: '0.75',
          cursor: 'not-allowed',
        },
      },
      pagination: {
        color: 'greens.3',
        bg: 'greens.4',
        borderColor: 'transparent',
        '&:hover': {
          opacity: '0.95',
        },
      },
      white: {
        color: 'oranges.2',
        bg: 'whites.0',
        border: 0,
        '&:hover': {
          backgroundColor: 'greys.2',
        },
      },
      blue: {
        color: 'white',
        bg: 'blues.2',
        borderColor: 'blues.2',
        '&:hover': {
          opacity: '0.95',
        },
        '&:disabled': {
          opacity: '0.75',
          cursor: 'not-allowed',
          color: 'white',
        },
      },
      aqua: {
        color: 'white',
        bg: 'aquas.0',
        borderColor: 'aquas.0',
        '&:hover': {
          color: 'aquas.0',
          borderColor: 'aquas.0',
        },
        '&:disabled': {
          color: 'aquas.2',
          borderColor: 'aquas.2',
          opacity: '0.75',
          cursor: 'not-allowed',
        },
      },
      plain: {
        color: 'blacks.0',
        bg: 'inherit',
        border: 'none',
        '&:hover': {
          opacity: '0.95',
        },
        '&:disabled': {
          opacity: '0.75',
          cursor: 'not-allowed',
          color: 'white',
        },
      },
      filter: {
        color: 'blacks.1',
        borderRadius: 100,
        px: 4,
        py: 2,
        '&:hover': {
          borderColor: 'sands.0',
        },
      },
    },
  }),
);

Wrapper.defaultProps = {
  borderRadius: '2px',
  px: 3,
  height: '40px',
  fontWeight: 1,
  fontSize: 1,
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: '1px',
  borderStyle: 'solid',
};

export const ButtonContent = styled.div<Props>`
  ${layout}
  ${flexbox}
  svg {
    ${space}
    width: 16px;
    height: 16px;
  }
`;

export const LoadingWrapper = styled.div<Props>`
  ${layout}
  svg {
    ${color}
    ${space}
    height: 24px !important;
  }
`;
